import { Box, Flex, Progress, Text } from '@chakra-ui/react';
import {
  faExclamationTriangle,
  faHourglassHalf
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@morressier/alembic';

export type ConsolidatedPdfGenerationStatus =
  | 'generating'
  | 'refreshing'
  | 'complete'
  | 'error'
  | null;

interface Props {
  isOpen: boolean;
  isGenerating: boolean;
  generationStatus: ConsolidatedPdfGenerationStatus;
  generationError?: string | null;
  onExit: () => void;
  onRetry: () => void;
  onContactSupport?: () => void;
}

const ConsolidatedPdfGenerationProgressModal: React.FC<Props> = ({
  isOpen,
  isGenerating,
  generationStatus,
  generationError,
  onExit,
  onRetry,
  onContactSupport
}) => (
  <Modal
    isOpen={isOpen}
    onClose={() => undefined}
    width="820px"
    closeOnEsc={false}
    closeOnOverlayClick={false}
    closeButtonProps={{ hidden: true }}
  >
    <Flex padding={8} flexDirection="column" gap={8}>
      <Flex
        width="100%"
        minHeight="56px"
        borderRadius="md"
        background={
          generationStatus === 'error'
            ? 'errorPrimaryFill'
            : 'neutralSecondaryFill'
        }
        alignItems="center"
        justifyContent="center"
        padding={8}
      >
        <FontAwesomeIcon
          icon={
            generationStatus === 'error'
              ? faExclamationTriangle
              : faHourglassHalf
          }
          fontSize="24px"
        />
      </Flex>
      <Box gap={4}>
        <Text textStyle="headingTitle" textAlign="center">
          {generationStatus === 'error'
            ? 'Unable to Generate Consolidated Submission Files'
            : 'Generating Consolidated Submission Files'}
        </Text>
        <Text textStyle="textContext" textAlign="center" mt={4}>
          {generationStatus === 'error'
            ? generationError ||
              'We could not create consolidated PDFs from your uploaded files. Your files are still saved. Please try again. If the issue persists, contact customer support.'
            : `We are currently creating consolidated PDFs from all your uploaded
          files. This process might take some time. If you encounter any
          issues, feel free to reach out to customer support.`}
        </Text>
      </Box>
      <Box background="white" borderRadius="md" padding={2}>
        <Flex justifyContent="space-between" mb={2}>
          <Text textStyle="headingItem">Consolidated Submission Files</Text>
          <Text textStyle="textContext">
            {generationStatus === 'complete'
              ? 'Complete'
              : generationStatus === 'error'
              ? 'Generation failed'
              : 'Merging ...'}
          </Text>
        </Flex>
        <Progress
          size="sm"
          colorScheme={generationStatus === 'error' ? 'red' : 'blue'}
          isIndeterminate={isGenerating}
          value={
            generationStatus === 'complete' || generationStatus === 'error'
              ? 100
              : 0
          }
        />
      </Box>
      {generationStatus === 'error' && (
        <Flex justifyContent="space-between" gap={2}>
          <Button variant="caution" onClick={onExit} disabled={isGenerating}>
            Exit
          </Button>
          <Flex justifyContent="flex-end" gap={3}>
            {onContactSupport && (
              <Button variant="secondary" onClick={onContactSupport}>
                Contact Customer Support
              </Button>
            )}
            <Button variant="primary" onClick={onRetry} disabled={isGenerating}>
              Retry
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  </Modal>
);

export default ConsolidatedPdfGenerationProgressModal;
